"use client";

import type { CSSProperties } from "react";

/**
 * Скелетон-состояние страницы view.
 *
 * Важно: это не отдельная "картинка загрузки", а облегчённый дубль геометрии
 * реальной страницы вида. Поэтому здесь намеренно повторяются контейнеры
 * карточки товара, ширины колонок, высота строк и двухэтажные заголовки групп:
 * когда данные приходят, серые плашки должны замениться таблицей без скачка
 * размеров и отступов.
 */

export type SkelCol = { key: string; w: number; label: string };
export type SkelGroup = { count: number };
export type SkelWidget = { id: number; c: number; r: number; w: number; h: number };
export type SkelColumnGroup = { label: string; cols: string[]; color?: string | null };

export function ViewPageSkeleton({
  skelGroups,
  skelCols,
  columnGroups = [],
  readOnly,
  compact = false,
  viewNeedsHorizontalStick = false,
  showSummary = false,
  widgets = [],
  widgetsRows = 2,
  embedded = false,
  showDashboard = true,
}: {
  skelGroups: SkelGroup[] | null;
  skelCols: SkelCol[];
  columnGroups?: SkelColumnGroup[];
  readOnly: boolean;
  compact?: boolean;
  viewNeedsHorizontalStick?: boolean;
  showSummary?: boolean;
  widgets?: SkelWidget[];
  widgetsRows?: 1 | 2;
  embedded?: boolean;
  showDashboard?: boolean;
}) {
  const groups = skelGroups ?? [{ count: 4 }, { count: 3 }, { count: 2 }];
  const hasWidgets = showDashboard && widgets.length > 0;
  const rowHeight = compact ? 26 : 36;
  const groupByCol: Record<string, SkelColumnGroup> = {};
  for (const group of columnGroups) {
    for (const key of group.cols) groupByCol[key] = group;
  }
  const headerSegments: Array<
    | { type: "solo"; col: SkelCol; start: number }
    | { type: "group"; label: string; cols: SkelCol[]; start: number; color?: string | null }
  > = [];
  for (let i = 0; i < skelCols.length;) {
    const col = skelCols[i];
    const group = groupByCol[col.key];
    if (!group) {
      headerSegments.push({ type: "solo", col, start: i });
      i += 1;
      continue;
    }
    const cols = [col];
    let j = i + 1;
    while (j < skelCols.length && groupByCol[skelCols[j].key]?.label === group.label) {
      cols.push(skelCols[j]);
      j += 1;
    }
    headerSegments.push({ type: "group", label: group.label, cols, start: i, color: group.color });
    i = j;
  }
  const hasColumnGroups = headerSegments.some(segment => segment.type === "group");
  const tableWidth = skelCols.reduce((sum, col) => sum + col.w, 0);
  const skeletonClass = "ui-skeleton";
  const softGroupBg = (color: string | null | undefined) => (
    color && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)
      ? `${color}1A`
      : "var(--row-odd-bg)"
  );
  const groupHeaderStickyStyle = viewNeedsHorizontalStick
    ? {
        left: "calc(var(--sidebar-w, 220px) + 24px)",
        transition: "left var(--sidebar-motion-ms, 300ms) cubic-bezier(0.16, 1, 0.3, 1)",
      }
    : undefined;
  const dashboardShellStyle = {
    position: "sticky",
    left: "calc(var(--sidebar-w, 220px) + 24px)",
    width: "max(0px, calc(100vw - var(--sidebar-w, 220px) - 48px))",
    transition: "left var(--sidebar-motion-ms, 300ms) cubic-bezier(0.16, 1, 0.3, 1)",
  } as const;
  return (
    <div style={embedded ? undefined : { paddingTop: "calc(var(--topbar-h, 76px) + 44px)" }}>
      <div
        className="w-full"
      >
        {showDashboard && (showSummary || hasWidgets) && (
          <div className="mt-6 mb-6" style={dashboardShellStyle}>
            <div className="mx-auto w-full max-w-[1140px] space-y-3">
              {showSummary && (
                <div className="grid grid-cols-2 gap-2 rounded-xl border border-gray-200 bg-white p-2 shadow-[0_1px_0_rgba(0,0,0,0.06)] sm:grid-cols-3 lg:grid-cols-6">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="rounded-lg border px-3 py-2" style={{ borderColor: "var(--table-border-light)", background: "var(--row-odd-bg)" }}>
                      <div className={`${skeletonClass} h-2.5 w-16 rounded`} />
                      <div className={`${skeletonClass} mt-2 h-4 w-20 rounded`} />
                    </div>
                  ))}
                </div>
              )}
              {hasWidgets && (
                <div
                  className="mb-4 grid gap-[8px]"
                  style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gridTemplateRows: `repeat(${widgetsRows}, 60px)` }}
                >
                  {widgets.map(widget => (
                    <div
                      key={widget.id}
                      className="rounded-2xl border px-2.5 py-1"
                      style={{ gridColumn: `${widget.c + 1}/span ${widget.w}`, gridRow: `${widget.r + 1}/span ${widget.h}`, borderColor: "var(--table-border)", backgroundColor: "rgba(250,250,250,1)" }}
                    >
                      <div className={`${skeletonClass} h-[9px] w-24 rounded`} />
                      <div className={`${skeletonClass} mt-2 h-4 w-20 rounded`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {!readOnly && (
          <div
            style={{
              position: "relative",
              zIndex: 25,
              width: tableWidth,
              minWidth: "100%",
              minHeight: 30,
              margin: "4px 0 8px",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                position: "sticky",
                left: "var(--sidebar-w, 220px)",
                width: "max(0px, calc(100vw - var(--sidebar-w, 220px)))",
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  maxWidth: 1140,
                  margin: "0 auto",
                  padding: "0 10px",
                  boxSizing: "border-box",
                }}
              >
                <div className="flex h-[28px] items-center gap-2">
                  <div className={`${skeletonClass} h-[28px] w-[86px] rounded-[8px]`} />
                  <div className={`${skeletonClass} h-[28px] w-[52px] rounded-[8px]`} />
                  <div className={`${skeletonClass} h-7 w-7 rounded-[8px]`} />
                  <div className={`${skeletonClass} h-7 w-7 rounded-lg`} />
                </div>
              </div>
            </div>
          </div>
        )}
        {groups.map((group, i) => (
          <div key={i} className={`px-6 pb-16 w-fit ${readOnly ? "" : "mx-auto"}`}>
            <div className="flex flex-col gap-3 group/item">
              <div
                className={`self-start w-0 overflow-visible${viewNeedsHorizontalStick ? " sticky" : ""}`}
                style={groupHeaderStickyStyle}
              >
                <div className="flex w-max items-end gap-3">
                  <div
                    className={`${skeletonClass} rounded-lg border shrink-0`}
                    style={{ width: 43, height: 54, boxShadow: "0 0 0 1px var(--table-border)" }}
                  />
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className={`${skeletonClass} h-4 w-48 rounded`} />
                      {group.count > 1 && <div className={`${skeletonClass} h-5 w-20 rounded-full`} />}
                    </div>
                    <div className="flex items-center gap-1.5 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
                      <div className={`${skeletonClass} h-5 w-16 rounded-md`} />
                      <div className={`${skeletonClass} h-5 w-20 rounded-md`} />
                      <div className={`${skeletonClass} h-5 w-12 rounded-md`} />
                    </div>
                  </div>
                </div>
              </div>
              <div data-view-width-source="table" className="self-start w-fit border rounded-[11px] shadow-[0_1px_0_rgba(0,0,0,0.08)]" style={{ background: "var(--row-even-bg)", borderColor: "var(--table-border)" }}>
                <table
                  className="text-sm"
                  style={{
                    color: "var(--table-text)",
                    "--view-row-h": `${rowHeight}px`,
                    width: tableWidth,
                    minWidth: tableWidth,
                    tableLayout: "fixed",
                  } as CSSProperties}
                >
                  <thead style={{ userSelect: "none" }}>
                    {!hasColumnGroups ? (
                      <tr style={{ background: "var(--row-odd-bg)", borderBottom: "1px solid var(--table-border)" }}>
                        {skelCols.map((c, j) => (
                          <th
                            key={c.key}
                            className={`px-4 py-2.5 text-left whitespace-nowrap font-medium text-xs${j === 0 ? " rounded-tl-[11px]" : ""}${j === skelCols.length - 1 ? " rounded-tr-[11px]" : ""}`}
                            style={{ width: c.w, minWidth: c.w, color: "var(--table-header-text)" }}
                          >
                            <div className={`${skeletonClass} h-3 rounded`} style={{ width: Math.min(c.w - 24, c.w * 0.7) }} />
                          </th>
                        ))}
                      </tr>
                    ) : (
                      <>
                        <tr style={{ background: "var(--row-odd-bg)", borderBottom: "1px solid var(--table-border)" }}>
                          {headerSegments.map((segment, j) => {
                            if (segment.type === "solo") {
                              return (
                                <th
                                  key={segment.col.key}
                                  rowSpan={2}
                                  className={`px-4 py-2.5 text-left whitespace-nowrap font-medium text-xs${j === 0 ? " rounded-tl-[11px]" : ""}${j === headerSegments.length - 1 ? " rounded-tr-[11px]" : ""}`}
                                  style={{ width: segment.col.w, minWidth: segment.col.w, color: "var(--table-header-text)", background: "var(--row-odd-bg)", borderBottom: "1px solid var(--table-border)" }}
                                >
                                  <div className={`${skeletonClass} h-3 rounded`} style={{ width: Math.min(segment.col.w - 24, segment.col.w * 0.7) }} />
                                </th>
                              );
                            }
                            const width = segment.cols.reduce((sum, col) => sum + col.w, 0);
                            return (
                              <th
                                key={`${segment.label}-${j}`}
                                colSpan={segment.cols.length}
                                className={`relative px-4 text-[10px] font-semibold uppercase tracking-wider leading-tight whitespace-normal break-words${j === 0 ? " rounded-tl-[11px]" : ""}${j === headerSegments.length - 1 ? " rounded-tr-[11px]" : ""}`}
                                style={{
                                  width,
                                  minWidth: width,
                                  textAlign: "center",
                                  paddingTop: 4,
                                  paddingBottom: 4,
                                  background: softGroupBg(segment.color),
                                  borderLeft: j > 0 ? "1px solid var(--table-border)" : undefined,
                                  borderRight: j < headerSegments.length - 1 ? "1px solid var(--table-border)" : undefined,
                                  borderBottom: "1px solid var(--table-border)",
                                  color: "var(--table-header-text)",
                                }}
                              >
                                <div className={`${skeletonClass} mx-auto h-2.5 max-w-[72px] rounded`} />
                              </th>
                            );
                          })}
                        </tr>
                        <tr style={{ background: "var(--row-odd-bg)", borderBottom: "1px solid var(--table-border)" }}>
                          {headerSegments.flatMap(segment => (
                            segment.type === "group"
                              ? segment.cols.map(c => (
                                  <th key={c.key} className="px-4 pt-0 pb-px text-left whitespace-nowrap font-medium text-xs" style={{ width: c.w, minWidth: c.w, color: "var(--table-header-text)" }}>
                                    <div className={`${skeletonClass} h-3 rounded`} style={{ width: Math.min(c.w - 24, c.w * 0.7) }} />
                                  </th>
                                ))
                              : []
                          ))}
                        </tr>
                      </>
                    )}
                  </thead>
                  <tbody>
                    {Array.from({ length: group.count }).map((_, r) => (
                      <tr
                        key={r}
                        className="border-b last:border-0 text-xs"
                        style={{
                          height: rowHeight,
                          "--row-bg": r % 2 !== 0 ? "var(--row-odd-bg)" : "var(--row-even-bg)",
                          background: r % 2 !== 0 ? "var(--row-odd-bg)" : "var(--row-even-bg)",
                          borderColor: "var(--table-border)",
                        } as CSSProperties}
                      >
                        {skelCols.map((c, j) => (
                          <td
                            key={c.key}
                            className="px-3 py-px whitespace-nowrap"
                            style={{
                              width: c.w,
                              minWidth: c.w,
                              ...(group.count > 1 && r > 0 ? { borderTop: "1px solid var(--table-border-light)" } : {}),
                            }}
                          >
                            <div className={`${skeletonClass} h-3 rounded`} style={{ width: Math.min(c.w - 20, c.w * (0.4 + (((i * 10 + r * 3 + j) % 6) / 10))) }} />
                          </td>
                        ))}
                      </tr>
                    ))}
                    {showSummary && group.count > 1 && (
                      <tr className="border-t text-xs is-summary" style={{ background: "var(--summary-row-bg)", height: rowHeight, borderColor: "var(--table-border)" }}>
                        {skelCols.map((c, j) => (
                          <td
                            key={c.key}
                            className={`px-3 py-1 whitespace-nowrap${j === 0 ? " rounded-bl-[11px]" : ""}${j === skelCols.length - 1 ? " rounded-br-[11px]" : ""}`}
                            style={{ width: c.w, minWidth: c.w }}
                          >
                            <div className={`${skeletonClass} h-3 rounded`} style={{ width: Math.min(c.w - 20, c.w * (0.36 + (((i * 8 + j) % 5) / 12))) }} />
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
